import React from 'react';
import { useHistory } from 'react-router';
import { useFormik } from 'formik';
import { Box, Button, FormControlLabel, Switch, TextField } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import VenueDeliveryConfigFormProps from 'types/forms/VenueDeliveryConfigFormProps';
import VenueDeliveryConfig, { DeliveryTimeRangeConfig } from 'types/models/VenueDeliveryConfig';
import PickUpVerificationMethod from 'types/models/PickUpVerificationMethod';
import VenueDeliveryConfigForm from './schemas/VenueDeliveryConfigForm';
import ConfirmDialog from 'components/ConfirmDialog';
import AreasInput from 'components/AreasInput';
import SelectField from 'components/SelectField';
import {
  destroyTimeRangeConfig,
  storeTimeRangeConfig,
  updateTimeRangeConfig,
  updateVenueDeliveryConfig,
} from 'services/venuesServices';
import { getPickUpVerificationMethods } from 'services/pickupVerificationMethodServices';
import { pickUpVerificationMethodsForSelect } from 'utils/delivery';
import 'styles/forms/VenueDeliveryConfigForm.scss';
import AddOrRemoveTimeRange from 'components/AddOrRemoveTimeRange';
import InfoBanner from 'components/InfoBanner';

export default ({ venue }: VenueDeliveryConfigFormProps): JSX.Element => {
  const history = useHistory();
  const [openDialog, setOpenDialog] = React.useState(false);
  const [pickUpVerificationMethods, setPickUpVerificationMethods] = React.useState<PickUpVerificationMethod[]>([]);
  const { t } = useTranslation();
  const codeVerificationMethods = pickUpVerificationMethodsForSelect(pickUpVerificationMethods, t);
  const [bannerMessage, setBannerMessage] = React.useState('');
  const [infoBannerResult, setInfoBannerResult] = React.useState<boolean>(false);

  const onConfirm = async (): Promise<void> => {
    if (!venue) return;

    const dataVenueDeliveryConfig: VenueDeliveryConfig = {
      id: '',
      deliveryEnabled: formik.values.deliveryEnabled,
      pickUpEnabled: formik.values.pickUpEnabled,
      isLocalDelivery: formik.values.isLocalDelivery,
      priorityOverCommerce: formik.values.priorityOverCommerce,
      showPhoneField: formik.values.showPhoneField,
      isPhoneFieldRequired: formik.values.isPhoneFieldRequired,
      pickUpVerificationMethod: formik.values.pickUpVerificationMethod,
      areas: formik.values.areas,
      timeRangeConfigs: formik.values.deliveryTimeRangeConfigs,
      purchaseToleranceTime: formik.values.purchaseToleranceTime,
    };

    try {
      const result = await updateVenueDeliveryConfig(venue.id, dataVenueDeliveryConfig);
      if (result) {
        history.go(-1);
      } else {
        throw new Error();
      }
    } catch (error) {
      formik.setSubmitting(false);
    }
  };

  const onCancel = (): void => {
    formik.setSubmitting(false);
  };

  const formik = useFormik({
    initialValues: {
      deliveryEnabled: venue.deliveryConfig?.deliveryEnabled ?? false,
      pickUpEnabled: venue.deliveryConfig?.pickUpEnabled ?? false,
      isLocalDelivery: venue.deliveryConfig?.isLocalDelivery ?? false,
      priorityOverCommerce: venue?.deliveryConfig?.priorityOverCommerce ?? false,
      showPhoneField: venue?.deliveryConfig?.showPhoneField ?? false,
      isPhoneFieldRequired: venue?.deliveryConfig?.isPhoneFieldRequired ?? false,
      pickUpVerificationMethod: venue?.deliveryConfig?.pickUpVerificationMethod ?? null,
      areas: venue?.deliveryConfig?.areas ?? [],
      purchaseToleranceTime: venue?.deliveryConfig?.purchaseToleranceTime ?? null,
      deliveryTimeRangeConfigs: venue.deliveryConfig?.timeRangeConfigs ?? [],
    },
    validationSchema: VenueDeliveryConfigForm(t),
    onSubmit: (): void => {
      setOpenDialog(true);
    },
  });

  const onDeleteTimeRangeConfigRow = async (ageRangeConfig: DeliveryTimeRangeConfig) => {
    if (!venue) return;
    const timeRangeConfigResult = await destroyTimeRangeConfig(venue.id, ageRangeConfig.id);
    if (timeRangeConfigResult !== null && venue.deliveryConfig && venue.deliveryConfig.timeRangeConfigs) {
      let values = formik.values.deliveryTimeRangeConfigs;
      values = formik.values.deliveryTimeRangeConfigs.filter((config) => config.id !== timeRangeConfigResult.id);
      formik.setFieldValue('deliveryTimeRangeConfigs', [...values]);

      setInfoBannerResult(false);
      setBannerMessage(t('venueDeliveryConfig-form-deleted-range-time'));
    }
  };

  const onAddTimeRangeConfigRow = async (timeRangeConfig: DeliveryTimeRangeConfig): Promise<void> => {
    if (!venue) return;
    const body = {
      fromHour: timeRangeConfig.fromHour,
      toHour: timeRangeConfig.toHour,
      venueConfigId: venue.deliveryConfig?.id,
    };

    const timeRangeConfigResult = await storeTimeRangeConfig(venue.id, body);
    if (timeRangeConfigResult !== null) {
      const values = formik.values.deliveryTimeRangeConfigs;
      values.push(timeRangeConfigResult);
      formik.setFieldValue('deliveryTimeRangeConfigs', [...values]);

      setInfoBannerResult(true);
      setBannerMessage(t('venueDeliveryConfig-form-created-range-time'));
    }
  };

  const onUpdateTimeRangeConfigRow = async (timeRangeConfig: DeliveryTimeRangeConfig): Promise<void> => {
    if (!venue) return;
    const body = {
      fromHour: timeRangeConfig.fromHour,
      toHour: timeRangeConfig.toHour,
      time: venue.deliveryConfig?.id,
    };

    const timeRangeConfigResult = await updateTimeRangeConfig(venue.id, body, timeRangeConfig.id);
    if (timeRangeConfigResult !== null) {
      const configIndex = venue.deliveryConfig?.timeRangeConfigs.findIndex(
        (config) => config.id === timeRangeConfigResult.id,
      );
      if (configIndex !== undefined && configIndex !== -1 && venue.deliveryConfig) {
        venue.deliveryConfig.timeRangeConfigs[configIndex] = timeRangeConfigResult;
      }
      setInfoBannerResult(true);
      setBannerMessage(t('venueDeliveryConfig-form-updated-range-time'));
    }
  };

  React.useEffect(() => {
    const getAllPickUpVerificationMethods = async () => {
      if (formik.values.pickUpEnabled) {
        const pickUpVerificationMethods = await getPickUpVerificationMethods();
        setPickUpVerificationMethods(pickUpVerificationMethods);
      } else {
        if (pickUpVerificationMethods.length > 0 || formik.values.pickUpVerificationMethod !== null) {
          setPickUpVerificationMethods([]);
          formik.setFieldValue('pickUpVerificationMethod', null);
        }
      }
    };
    getAllPickUpVerificationMethods();
  }, [formik.values.pickUpEnabled]);

  return (
    <>
      <InfoBanner
        message={bannerMessage}
        result={infoBannerResult}
        onClose={() => {
          setInfoBannerResult(false);
          setBannerMessage('');
        }}
      />
      <ConfirmDialog open={openDialog} setOpen={setOpenDialog} onConfirm={onConfirm} onCancel={onCancel} />
      <form onSubmit={formik.handleSubmit}>
        <Box>
          <FormControlLabel
            id="deliveryEnabled"
            name="deliveryEnabled"
            control={<Switch checked={formik.values.deliveryEnabled} onChange={formik.handleChange} />}
            label={t('venueDeliveryConfig-form-deliveryEnabled')}
            disabled={formik.isSubmitting}
          />
        </Box>
        <Box>
          <FormControlLabel
            id="pickUpEnabled"
            name="pickUpEnabled"
            control={<Switch checked={formik.values.pickUpEnabled} onChange={formik.handleChange} />}
            label={t('venueDeliveryConfig-form-pickUpEnabled')}
            disabled={formik.isSubmitting}
          />
        </Box>
        {formik.values.deliveryEnabled && (
          <Box>
            <FormControlLabel
              id="isLocalDelivery"
              name="isLocalDelivery"
              control={<Switch checked={formik.values.isLocalDelivery} onChange={formik.handleChange} />}
              label={t('venueDeliveryConfig-form-isLocalDelivery')}
              disabled={formik.isSubmitting}
            />
          </Box>
        )}
        <Box>
          <FormControlLabel
            id="priorityOverCommerce"
            name="priorityOverCommerce"
            control={<Switch checked={formik.values.priorityOverCommerce} onChange={formik.handleChange} />}
            label={t('venueDeliveryConfig-form-priorityOverCommerce')}
            disabled={formik.isSubmitting}
          />
        </Box>
        {formik.values.deliveryEnabled && (
          <Box>
            <FormControlLabel
              id="showPhoneField"
              name="showPhoneField"
              control={<Switch checked={formik.values.showPhoneField} onChange={formik.handleChange} />}
              label={t('venueDeliveryConfig-form-showPhoneField')}
              disabled={formik.isSubmitting}
            />
            {formik.values.showPhoneField && (
              <FormControlLabel
                id="isPhoneFieldRequired"
                name="isPhoneFieldRequired"
                control={<Switch checked={formik.values.isPhoneFieldRequired} onChange={formik.handleChange} />}
                label={t('venueDeliveryConfig-form-isPhoneFieldRequired')}
                disabled={formik.isSubmitting}
              />
            )}
          </Box>
        )}
        {formik.values.pickUpEnabled && (
          <SelectField
            name="pickUpVerificationMethod"
            label={t('venueDeliveryConfig-form-pickUpVerificationMethodConfig')}
            placeholder={t('venueDeliveryConfig-form-pickUpVerificationMethodConfig-placeholder')}
            value={formik.values.pickUpVerificationMethod}
            options={codeVerificationMethods}
            isMulti={false}
            setFieldValue={formik.setFieldValue}
            onBlur={formik.handleBlur}
            touched={formik.touched.pickUpVerificationMethod}
            error={formik.errors.pickUpVerificationMethod}
            isClearable={true}
            backspaceRemovesValue={false}
            isDisabled={formik.isSubmitting}
            isSearchable={true}
          />
        )}

        {(formik.values.deliveryEnabled || formik.values.pickUpEnabled) && (
          <>
            <TextField
              fullWidth
              margin="dense"
              id="purchaseToleranceTime"
              name="purchaseToleranceTime"
              label={t('venueDeliveryConfig-form-purchase-tolerance-time')}
              value={formik.values.purchaseToleranceTime}
              onChange={formik.handleChange}
              type="number"
            />

            {venue.deliveryConfig?.timeRangeConfigs && (
              <AddOrRemoveTimeRange
                onDeleteRow={onDeleteTimeRangeConfigRow}
                onAddRow={onAddTimeRangeConfigRow}
                onUpdateRow={onUpdateTimeRangeConfigRow}
                inputName={'deliveryTimeRangeConfigs'}
                values={formik.values.deliveryTimeRangeConfigs}
                setFieldValue={formik.setFieldValue}
              />
            )}
          </>
        )}

        <AreasInput
          type={'Venue'}
          name={'areas'}
          values={formik.values.areas}
          setFieldValue={formik.setFieldValue}
          isDisabled={formik.isSubmitting}
        />

        {formik.errors.areas && <div className="fields-required-alert">{t('general-suggestions.field-required')}</div>}

        <Box marginTop="1rem">
          <Button className="btn-50" variant="contained" color="primary" type="submit" disabled={formik.isSubmitting}>
            {t('save-config')}
          </Button>
        </Box>
      </form>
    </>
  );
};
